import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface ForgetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const ForgetPasswordModal: React.FC<ForgetPasswordModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);

  // 弹窗关闭时重置确认步骤状态
  useEffect(() => {
    if (!isOpen) {
      setShowConfirm(false);
    }
  }, [isOpen]);

  // 点击"重设密码"：先弹出确认弹窗，提示需要重新登录软件
  const handleResetClick = () => {
    setShowConfirm(true);
  };

  // 确认重置：关闭确认弹窗并执行重设流程（退出登录）
  const handleConfirmReset = () => {
    setShowConfirm(false);
    onConfirm();
  };

  // 关闭主弹窗
  const handleClose = () => {
    setShowConfirm(false);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* 主弹窗：重置解锁密码
          createPortal 渲染到 body：编辑器外层（EditorModeHost 轨道）带 CSS transform，
          fixed 定位会以该 transform 祖先为基准导致弹窗不在画面中间，portal 到 body 后
          相对视口居中，桌面端与移动端均显示正确 */}
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md p-6 border border-gray-200 dark:border-gray-700 max-h-[90vh] overflow-y-auto">
          {/* 标题 */}
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
              <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              重置解锁密码
            </h2>
          </div>

          {/* 说明内容 */}
          <div className="space-y-4 mb-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                需要通过 <strong>重新登录应用</strong> 验证身份后重置密码。
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <strong className="text-blue-600 dark:text-blue-400">提示：</strong>
                  重新登录后，3 秒钟内会自动弹出修改密码窗口，可以取消修改。
                </p>
              </div>
            </div>
          </div>

          {/* 按钮 */}
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
            >
              取消
            </button>
            <button
              onClick={handleResetClick}
              className="flex-1 px-4 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              重设密码
            </button>
          </div>
        </div>
      </div>

      {/* 确认弹窗：提示用户将需要重新登录软件 */}
      {showConfirm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm p-6 border border-gray-200 dark:border-gray-700">
            {/* 标题 */}
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                确认重置密码
              </h3>
            </div>

            {/* 警告内容 */}
            <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg mb-6">
              <p className="text-sm text-orange-700 dark:text-orange-300">
                重置密码需要 <strong>重新登录软件</strong>，确认后当前登录状态将退出，
                您需要重新登录账号后才能继续使用。
              </p>
            </div>

            {/* 按钮 */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
              >
                取消
              </button>
              <button
                onClick={handleConfirmReset}
                className="flex-1 px-4 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                确定，重新登录
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default ForgetPasswordModal;
